/**
 * CallDetailPanel — slide-in side panel (3/4 width) showing call details.
 * Sections: Call Info, Recordings, Call History (for this number), Comments/Notes.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  X,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  User,
  MessageCircle,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCallStore } from '@/stores/call-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { useAuthStore } from '@/stores/auth-store';
import { CallRecordings } from './call-recordings';
import { CallNotes } from './call-notes';
import type { CallHistoryItem } from '@/types/voice';

interface CallDetailPanelProps {
  item: CallHistoryItem;
  onClose: () => void;
  metadata?: any;
  onUpdateMetadata?: (updates: { label?: string | null; assignee?: string | null }) => void;
  allLabels?: Array<{ value: string; label: string; color: string }>;
}

export function CallDetailPanel({ item, onClose, metadata, onUpdateMetadata, allLabels = [] }: CallDetailPanelProps) {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const DirectionIcon = item.status === 'no-answer' || item.status === 'missed'
    ? PhoneMissed
    : item.isIncoming ? PhoneIncoming : PhoneOutgoing;

  const directionColor = item.status === 'no-answer' || item.status === 'missed'
    ? 'text-destructive'
    : item.isIncoming ? 'text-blue-500' : 'text-emerald-500';

  const durationSec = parseInt(item.duration) || 0;
  const mins = Math.floor(durationSec / 60);
  const secs = durationSec % 60;
  const durationStr = durationSec > 0 ? `${mins}m ${secs}s` : 'N/A';

  const timeStr = (() => {
    try {
      return new Date(item.time).toLocaleString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return item.time;
    }
  })();

  const [activeTab, setActiveTab] = useState<'details' | 'recordings' | 'related'>('details');

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]"
        onClick={onClose}
      />

      {/* Panel — 3/4 width, slide from right */}
      <div className="fixed top-0 right-0 bottom-0 z-50 w-3/4 max-w-[900px] bg-background border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border shrink-0">
          <div className={cn('flex h-10 w-10 items-center justify-center rounded-full bg-muted/50')}>
            <DirectionIcon className={cn('h-5 w-5', directionColor)} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground truncate flex items-center gap-2">
              {item.callChannel === 'whatsapp' && (
                <MessageCircle className="h-4 w-4 text-green-500 shrink-0" />
              )}
              {item.customerNumber?.replace('whatsapp:', '')}
            </h2>
            <p className="text-xs text-muted-foreground">
              {item.isIncoming ? 'Incoming' : 'Outgoing'} call · {timeStr}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-5 py-2 border-b border-border shrink-0">
          <button
            onClick={() => setActiveTab('details')}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              activeTab === 'details'
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('recordings')}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              activeTab === 'recordings'
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            Recordings
          </button>
          <button
            onClick={() => setActiveTab('related')}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              activeTab === 'related'
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            Related Calls
          </button>
        </div>

        {/* Content */}
        {activeTab === 'details' && (
          <div className="flex-1 flex overflow-hidden">
            {/* Left half: Call Info + History */}
            <div className="flex-1 overflow-y-auto border-r border-border">
              {/* Label & Assignee dropdowns */}
              {onUpdateMetadata && (
                <div className="px-5 py-3 flex items-center gap-3">
                  <LabelDropdown
                    value={metadata?.label || null}
                    labels={allLabels}
                    onChange={(label) => onUpdateMetadata({ label })}
                  />
                  <AssigneeDropdown
                    value={metadata?.assignee || item.agentEmail || null}
                    onChange={(assignee) => onUpdateMetadata({ assignee })}
                  />
                </div>
              )}
              <CallInfoSection item={item} durationStr={durationStr} timeStr={timeStr} />
            </div>

            {/* Right half: Notes */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-5 pt-3 pb-1 shrink-0">
                <h3 className="text-sm font-semibold text-foreground">Notes</h3>
              </div>
              <div className="flex-1 overflow-y-auto px-5 pb-3">
                {/* Read every leg: after a transfer each agent's notes sit
                    under their own leg, and the row only names one of them. */}
                <CallNotes
                  callSid={item.id}
                  extraSids={[
                    ...(item.legSids || []),
                    // The viewer's own leg. On an OUTBOUND call it is the
                    // parent (client:<agent> → customer), which legSids never
                    // contains, so without this an agent's own notes on their
                    // outbound calls are unreachable — and the badge, which
                    // does count that leg, would disagree with this list.
                    ...(item.myLegSids || []),
                    // The leg the Voice SDK gave the agent on an outbound call.
                    // myLegSids only resolves it when Twilio returned it in the
                    // same page, which the phone-number search never does.
                    ...(item.parentCallSid ? [item.parentCallSid] : []),
                    // Legs added by a transfer — Twilio gives them no link
                    // back to this call, so they are recorded when the
                    // transfer completes and are the only way a
                    // transferred-to agent finds the note they wrote.
                    ...((metadata?.transferLegs || []) as Array<{ sid: string }>).map((l) => l.sid),
                  ]}
                  customerNumber={item.customerNumber}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'recordings' && (
          <div className="flex-1 overflow-y-auto p-5">
            <CallRecordings callId={item.id} />
          </div>
        )}

        {activeTab === 'related' && (
          <div className="flex-1 overflow-y-auto p-5">
            <RelatedCallsPanel callSid={item.id} parentCallSid={item.parentCallSid} />
          </div>
        )}
      </div>
    </>
  );
}

// ── Call Info Section ────────────────────────────────────────────────

function CallInfoSection({
  item,
  durationStr,
  timeStr,
}: {
  item: CallHistoryItem;
  durationStr: string;
  timeStr: string;
}) {
  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const currentUser = useAuthStore((s) => s.user);

  const agentUser = item.agentEmail
    ? Object.values(orgUsers).find((u) => u.email === item.agentEmail)
    : null;
  const agentName = agentUser?.name || item.agentEmail || 'Unknown';
  const agentPic = agentUser?.picture
    || (item.agentEmail === currentUser?.email ? currentUser?.photoURL : null);

  return (
    <div className="p-5 space-y-4">
      {/* Info rows — label above value */}
      <div className="space-y-3 text-xs">
        <div>
          <p className="text-muted-foreground text-[11px]">Date & Time</p>
          <p className="text-foreground font-medium mt-0.5">{timeStr}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-[11px]">Duration</p>
          <p className="text-foreground font-medium mt-0.5">{durationStr}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-[11px]">{item.isIncoming ? (item.status === 'no-answer' || item.status === 'missed' ? 'Agent' : 'Answered by') : 'Caller'}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {agentPic ? (
              <img src={agentPic} alt="" className="h-5 w-5 rounded-full object-cover" />
            ) : (
              <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-semibold text-primary">
                {agentName[0]?.toUpperCase()}
              </div>
            )}
            <span className="text-foreground font-medium">{agentName}</span>
          </div>
        </div>
        <div>
          <p className="text-muted-foreground text-[11px]">Direction</p>
          <p className="text-foreground font-medium mt-0.5">{item.isIncoming ? 'Inbound' : 'Outbound'}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-[11px]">Status</p>
          <p className="text-foreground font-medium mt-0.5">{formatStatus(item.status)}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-[11px]">Caller ID</p>
          <p className="text-foreground font-medium mt-0.5">{item.companyCallerId}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-[11px]">Cost</p>
          <p className="text-foreground font-medium mt-0.5">{item.price}</p>
        </div>
        {item.callChannel === 'whatsapp' && (
          <div>
            <p className="text-muted-foreground text-[11px]">Channel</p>
            <p className="text-green-600 dark:text-green-400 font-medium mt-0.5">WhatsApp</p>
          </div>
        )}
        <div>
          <p className="text-muted-foreground text-[11px]">Call SID</p>
          <p className="text-foreground font-mono text-[11px] mt-0.5">{item.id}</p>
        </div>
      </div>

      {/* Call history for this customer number */}
      <CustomerCallHistoryPanel customerNumber={item.customerNumber} currentCallId={item.id} />
    </div>
  );
}

// ── Customer Call History Panel ──────────────────────────────────────

function CustomerCallHistoryPanel({ customerNumber, currentCallId }: { customerNumber: string; currentCallId: string }) {
  const callHistory = useCallStore((s) => s.callHistory);

  const displayCalls = useMemo(
    () => callHistory.filter((c) => c.customerNumber === customerNumber && c.id !== currentCallId),
    [callHistory, customerNumber, currentCallId]
  );

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground">
          Call History — {customerNumber?.replace('whatsapp:', '')}
        </h3>
      </div>

      {displayCalls.length === 0 && (
        <p className="text-xs text-muted-foreground py-8 text-center">No other calls with this number</p>
      )}

      <div className="space-y-1">
        {displayCalls.map((call) => {
          const Icon = call.status === 'no-answer' || call.status === 'missed'
            ? PhoneMissed
            : call.isIncoming ? PhoneIncoming : PhoneOutgoing;
          const iconColor = call.status === 'no-answer' || call.status === 'missed'
            ? 'text-destructive'
            : call.isIncoming ? 'text-blue-500' : 'text-emerald-500';
          const dur = parseInt(call.duration) || 0;
          const durStr = dur > 0
            ? `${Math.floor(dur / 60)}m ${dur % 60}s`
            : '--';
          const timeStr = (() => {
            try {
              return new Date(call.time).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
            } catch { return call.time; }
          })();

          const agentName = call.agentEmail ? call.agentEmail.split('@')[0] : null;
          const agentInitial = agentName ? agentName[0].toUpperCase() : null;
          const COLORS = [
            { bg: 'bg-blue-500/15', text: 'text-blue-600 dark:text-blue-400' },
            { bg: 'bg-emerald-500/15', text: 'text-emerald-600 dark:text-emerald-400' },
            { bg: 'bg-violet-500/15', text: 'text-violet-600 dark:text-violet-400' },
            { bg: 'bg-amber-500/15', text: 'text-amber-600 dark:text-amber-400' },
            { bg: 'bg-rose-500/15', text: 'text-rose-600 dark:text-rose-400' },
            { bg: 'bg-cyan-500/15', text: 'text-cyan-600 dark:text-cyan-400' },
          ];
          const colorIdx = call.agentEmail ? call.agentEmail.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length : 0;
          const avatarColor = COLORS[colorIdx];

          return (
            <div key={call.id} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-muted/30 transition-colors">
              <Icon className={cn('h-4 w-4 shrink-0', iconColor)} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground">{timeStr}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[11px] text-muted-foreground">{durStr}</span>
                  {agentInitial ? (
                    <div className="flex items-center gap-1" title={call.agentEmail || ''}>
                      <div className={cn('h-4 w-4 rounded-full flex items-center justify-center text-[8px] font-semibold', avatarColor.bg, avatarColor.text)}>
                        {agentInitial}
                      </div>
                      <span className="text-[11px] text-muted-foreground truncate max-w-[80px]">{agentName}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <div className="h-4 w-4 rounded-full bg-muted/50 flex items-center justify-center">
                        <User className="h-2.5 w-2.5 text-muted-foreground/40" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Label Dropdown ──────────────────────────────────────────────────

function LabelDropdown({
  value,
  labels,
  onChange,
}: {
  value: string | null;
  labels: Array<{ value: string; label: string; color: string }>;
  onChange: (label: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const activeLabel = labels.find((l) => l.value === value);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors"
      >
        {activeLabel ? (
          <>
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: activeLabel.color }} />
            <span style={{ color: activeLabel.color }} className="font-medium">{activeLabel.label}</span>
          </>
        ) : (
          <span className="text-muted-foreground">No Label</span>
        )}
        <ChevronDown className="h-3 w-3 text-muted-foreground" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-50 w-40 rounded-lg border border-border bg-card shadow-lg py-1">
            {value && (
              <button
                onClick={() => { onChange(null); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-[11px] text-muted-foreground hover:bg-muted/50"
              >
                Remove label
              </button>
            )}
            {labels.map((l) => (
              <button
                key={l.value}
                onClick={() => { onChange(l.value); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium hover:bg-muted/50 transition-colors"
              >
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
                <span style={value === l.value ? { color: l.color } : undefined}>{l.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Assignee Dropdown ───────────────────────────────────────────────

function AssigneeDropdown({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (assignee: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const currentUser = useAuthStore((s) => s.user);

  const userList = useMemo(() => {
    const users = Object.values(orgUsers).map((u) => ({ email: u.email, name: u.name, picture: u.picture }));
    if (currentUser?.email && !users.some((u) => u.email === currentUser.email)) {
      users.unshift({ email: currentUser.email, name: currentUser.displayName || currentUser.email.split('@')[0], picture: currentUser.photoURL || undefined });
    }
    return users;
  }, [orgUsers, currentUser]);

  const assignedUser = value ? userList.find((u) => u.email === value) : null;
  const displayName = assignedUser?.name || (value ? value.split('@')[0] : null);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors"
      >
        {assignedUser?.picture ? (
          <img src={assignedUser.picture} alt="" className="h-4 w-4 rounded-full object-cover" />
        ) : displayName ? (
          <div className="h-4 w-4 rounded-full bg-primary/10 flex items-center justify-center text-[8px] font-semibold text-primary">
            {displayName[0]?.toUpperCase()}
          </div>
        ) : (
          <User className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <span className={displayName ? 'text-foreground font-medium' : 'text-muted-foreground'}>
          {displayName || 'Unassigned'}
        </span>
        <ChevronDown className="h-3 w-3 text-muted-foreground" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-50 w-52 rounded-lg border border-border bg-card shadow-lg py-1 max-h-48 overflow-y-auto">
            {value && (
              <button
                onClick={() => { onChange(null); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-[11px] text-muted-foreground hover:bg-muted/50"
              >
                Unassign
              </button>
            )}
            {userList.map((u) => (
              <button
                key={u.email}
                onClick={() => { onChange(u.email); setOpen(false); }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-muted/50 transition-colors',
                  value === u.email && 'bg-primary/5'
                )}
              >
                {u.picture ? (
                  <img src={u.picture} alt="" className="h-5 w-5 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[9px] font-semibold text-muted-foreground shrink-0">
                    {(u.name || u.email)[0]?.toUpperCase()}
                  </div>
                )}
                <span className="truncate font-medium">{u.name || u.email.split('@')[0]}</span>
                {u.email === currentUser?.email && <span className="text-muted-foreground/50 text-[10px]">(you)</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Related Calls Panel ─────────────────────────────────────────────

function RelatedCallsPanel({ callSid, parentCallSid }: { callSid: string; parentCallSid?: string }) {
  const api = useCallStore((s) => s.api);
  const accountSid = useCallStore((s) => s.accountSid);
  const [relatedCalls, setRelatedCalls] = useState<any[] | null>(null);
  const loading = relatedCalls === null;

  useEffect(() => {
    if (!api || !accountSid) return;
    if (relatedCalls !== null) return; // already fetched

    let mounted = true;
    (async () => {
      try {
        // If this call has a parent, fetch siblings using parent's SID; otherwise fetch children using this call's SID
        const lookupSid = parentCallSid || callSid;
        const resp = await api.getCalls(accountSid, `PageSize=20&ParentCallSid=${lookupSid}`);
        const children = resp.data?.calls || [];

        const related: any[] = [];

        // If we looked up by parent, add the parent call
        if (parentCallSid) {
          const localParent = useCallStore.getState().callHistory.find((c) => c.id === parentCallSid);
          if (localParent) {
            related.push({
              sid: localParent.id,
              from: localParent.isIncoming ? localParent.customerNumber : localParent.companyCallerId,
              to: localParent.isIncoming ? localParent.companyCallerId : localParent.customerNumber,
              direction: localParent.isIncoming ? 'inbound' : 'outbound-api',
              duration: localParent.duration,
              status: localParent.status,
              start_time: localParent.time,
              _relation: 'Parent',
            });
          } else {
            // Parent not in local history — fetch it
            try {
              const parentResp = await api.getCallsByUrl(
                `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/Calls/${parentCallSid}.json`
              );
              if (parentResp.data) {
                related.push({ ...parentResp.data, _relation: 'Parent' });
              }
            } catch { /* parent may not exist */ }
          }
        }

        children.forEach((c: any) => {
          const relation = parentCallSid
            ? (c.sid === callSid ? 'Current' : 'Sibling')
            : 'Child';
          related.push({ ...c, _relation: relation });
        });

        // Sort by time descending
        related.sort((a, b) => {
          const tA = new Date(a.start_time || 0).getTime();
          const tB = new Date(b.start_time || 0).getTime();
          return tB - tA;
        });

        if (mounted) setRelatedCalls(related);
      } catch (err) {
        console.error('[RelatedCalls] Failed:', err);
        if (mounted) setRelatedCalls([]);
      }
    })();

    return () => { mounted = false; };
  }, [api, accountSid, callSid]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 justify-center py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-xs">Loading related calls...</span>
      </div>
    );
  }

  if (!loading && relatedCalls && relatedCalls.length === 0) {
    return (
      <div className="text-center py-12">
        <Phone className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No related calls found</p>
        <p className="text-xs text-muted-foreground/60 mt-1">This call has no parent or child legs</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground mb-3">
        {relatedCalls.length} related call{relatedCalls.length !== 1 ? 's' : ''} found
      </p>
      {relatedCalls.map((call) => {
        const isIncoming = call.direction === 'inbound';
        const Icon = call.status === 'no-answer' || call.status === 'canceled'
          ? PhoneMissed
          : isIncoming ? PhoneIncoming : PhoneOutgoing;
        const iconColor = call.status === 'no-answer' || call.status === 'canceled'
          ? 'text-destructive'
          : isIncoming ? 'text-blue-500' : 'text-emerald-500';
        const dur = parseInt(call.duration) || 0;
        const durStr = dur > 0 ? `${Math.floor(dur / 60)}m ${dur % 60}s` : '--';
        const timeStr = (() => {
          try {
            return new Date(call.start_time).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
          } catch { return call.start_time; }
        })();

        const relationColor = call._relation === 'Parent'
          ? 'bg-violet-500/10 text-violet-600 dark:text-violet-400'
          : call._relation === 'Child'
            ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
            : 'bg-amber-500/10 text-amber-600 dark:text-amber-400';

        return (
          <div key={call.sid} className="flex items-center gap-3 rounded-lg border border-border p-3 hover:bg-muted/20 transition-colors">
            <Icon className={cn('h-4 w-4 shrink-0', iconColor)} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-foreground truncate">
                  {call.from} → {call.to}
                </p>
                <span className={cn('text-[10px] font-medium rounded-full px-2 py-0.5', relationColor)}>
                  {call._relation}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[11px] text-muted-foreground">{timeStr}</span>
                <span className="text-[11px] text-muted-foreground">{durStr}</span>
                <span className={cn(
                  'text-[10px]',
                  call.status === 'completed' ? 'text-emerald-500' :
                  call.status === 'no-answer' || call.status === 'canceled' ? 'text-destructive' :
                  'text-muted-foreground'
                )}>
                  {formatStatus(call.status)}
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground/50 font-mono mt-0.5">{call.sid}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function formatStatus(status: string): string {
  switch (status) {
    case 'completed': return 'Completed';
    case 'no-answer': return 'No Answer';
    case 'missed': return 'Missed';
    case 'busy': return 'Busy';
    case 'failed': return 'Failed';
    case 'canceled': return 'Canceled';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
}
